import { FormControl } from '@angular/forms';
import { IQueryParams } from './queryparams';
import { IBaseModel } from './base.model';
import { IBankDetail, IntermediaryType } from './broker.model';
import { IBeneficiary } from './beneficiary.models';

export type ClientDocumentType =
  | 'Other'
  | 'Passport'
  | 'Nrc'
  | 'DrivingLicense'
  | 'Certificate';

export enum ClientIdType {
  Nrc = 'Nrc',
  Passport = 'Passport',
}

export enum Gender {
  Unspecified = 'Unspecified',
  Male = 'Male',
  Female = 'Female',
}

export enum ClientStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum ClientType {
  Corporate = 'Corporate',
  Individual = 'Individual',
}

export interface ClientQueryParams extends IQueryParams {
  firstName?: string;
  lastName?: string;
  query?: string | null;
  idNumber?: string;
  email?: string;
  phoneNumber?: string;
  clientType?: string;
  id?: number | null;
  intermediaryId: number | null;
  insurerId?: number | null;
  branchId: number | null;
  partnerId?: number | null;
  userId?: number | null;
}

export interface IOtpRequest {
  otpCode: string;
  // the phone/email the OTP was sent to; filled from otp-contact when not given
  phoneNumber?: string | null;
  email?: string | null;
}

export interface IClientsRequest {
  email: string | null;
  bankDetail: {
    name?: string;
    branch?: string;
    accountNumber?: string;
  };
  phoneNumber?: string;
  physicalAddress: string;
  postalAddress: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  idNumber: string;
  idType: string;
  address: string;
  gender: string;
  password: string;
  insurerId?: number | null;
  intermediaryId?: number | null;
  partnerId?: number | null;
  taxPayerId?: string | null;
}

export interface ISelfServiceClientRequest extends IClientsRequest {
  insurerId?: number | null;
  partnerId?: number | null;
  intermediaryId?: number | null;
}

export interface ILookupRequest {
  insurerId: number | null;
  identifier: string;
  clientType: ClientType;
}

export interface IAssociateClientRequest {
  insurerId: number | null;
  intermediaryId: number | null;
  intermediaryType: IntermediaryType | null;
  clientId: number | null;
  clientType: ClientType;
}

export interface IAssociateClientResponse {
  status: string;
  message: string;
  clientDetails: IClient;
}

export interface IndividualClientUpdateRequest {
  firstName: string | null;
  lastName: string | null;
  idNumber: string | null;
  email: string | null;
  phoneNumber: string | null;
  physicalAddress: string | null;
  gender: Gender | null;
  bankDetail: IBankDetail | null;
  taxPayerId: string | null;
  dateOfBirth: Date | string | null;
}

export interface IGeneralForm {
  email: FormControl<string>;
  phoneNumber: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  idNumber: FormControl<string>;
  gender: FormControl<Gender>;
  physicalAddress: FormControl<string>;
}

export interface IBankForm {
  name: FormControl<string>;
  branch: FormControl<string>;
  accountNumber: FormControl<string>;
}

export interface IClient extends IBaseModel {
  name: string;
  email: string;
  phoneNumber: string;
  physicalAddress: string;
  status: ClientStatus;
  type: ClientType;
  createdAt: Date;
  updatedAt: Date;
  bankDetail?: {
    name?: string;
    branch?: string;
    accountNumber?: string;
  };
  dateOfBirth?: Date | null;
  identificationNumber?: string;
  documents: IClientDocument[];
  beneficiaries: IBeneficiary[];
}

export interface IIndividualClient extends IClient {
  firstName: string;
  lastName: string;
  idNumber: string;
  gender: Gender;
  taxPayerId?: number;
}

export interface IClientDocument extends IBaseModel {
  description: string;
  url: string;
  type: ClientDocumentType;
}

export interface IClientDocumentUploadRequest {
  description: string;
  files: File[];
  type: ClientDocumentType;
}

export function getClientName(client: IClient) {
  if (client.type === ClientType.Individual) {
    const individualClient = client as IIndividualClient;
    return `${individualClient.firstName} ${individualClient.lastName}`;
  }
  return client.name;
}

export interface ILookupResponse {
  identifier: string | null;
  name: string | null;
  id: number | null;
  message: string;
  isRegistered: boolean;
}
